import React, { useEffect, useState, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Form,
  FormGroup,
  Label,
  Button,
  Row,
  Col,
} from 'reactstrap';
import { useUser } from 'context/UserContext';
import { useAlertPlus, useConfirm } from 'hooks';
import Required from 'components/Required';
import ConfirmModal from 'components/ConfirmModal';
import AlertModal from 'components/AlertModal';
import {
  getAllConsultoriosCokiba,
  putHorariosConsultorio,
} from '../../features/consultorios/consultoriosAPI';
import { ConsultorioCokiba, HorariosAtencion, HorariosAtencionRegistrado } from 'types/Consultorio';
import HorariosAtencionComponent from 'components/Consultorios/HorariosAtencion';
import { horariosRegistradosFromConsultorios, validarSolapamientos } from '../../utils/horariosValidacion';

export default function EditHorarios() {
  const { id } = useParams();
  const { getMatricula } = useUser();
  const matriculateId = getMatricula();
  if (!matriculateId) {
    return null;
  }

  const history = useHistory();

  const [disableSend, setDisableSend] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const [showAlert, message, alertCallback, alert] = useAlertPlus('');
  const [confirm, confirmTitle, confirmAction, setConfirm] = useConfirm(false);

  const formElement = useRef<HTMLFormElement | null>(null);

  const [consultorio, setConsultorio] = useState<ConsultorioCokiba>({
    atiendeOS: false,
    codigoPostal: '',
    departamento: '',
    diasAtencion: '',
    direccion: '',
    idConsultorio: 0,
    idKinesiologo: 0,
    localidad: '',
    numero: 0,
    numeroTelefono: '',
    piso: '',
    prefijoTelefono: '',
    nombreConsultorio: '',
    idConsultorioHabilitado: null,
    horarios: [],
  });
  const [horariosAtencion, setHorariosAtencion] = useState<HorariosAtencion[]>([]);
  const [otrosHorarios, setOtrosHorarios] = useState<HorariosAtencionRegistrado[]>([]);

  useEffect(() => {
    setLoading(true);

    const loadData = async () => {
      try {
        const consultorios: ConsultorioCokiba[] = await getAllConsultoriosCokiba() as ConsultorioCokiba[];

        if (consultorios) {
          const dataHorarios: HorariosAtencion[] = [];

          consultorios.forEach(consultorio => {
            if (consultorio.idConsultorio == id) {
              dataHorarios.push(
                ...(consultorio.horarios ?? []).map(horario => ({
                  diaSemana: horario.diaSemana,
                  horaInicio: horario.horaInicio,
                  horaFin: horario.horaFin,
                })),
              );
              setConsultorio(consultorio);
            }
          });

          setHorariosAtencion(dataHorarios);
          setOtrosHorarios(horariosRegistradosFromConsultorios(consultorios, Number(id)));
        }
      } catch (err) {
        setDisableSend(true);
        alert(
          'Se produjo un error al intentar recuperar información inicial. ' +
            'Por favor, intente nuevamente más tarde.',
        );
        console.error('ConsultoriosCreate', 'Error al cargar datos iniciales');
        console.info(err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  const validarHorarios = (): boolean => {
    const resultado = validarSolapamientos(horariosAtencion, otrosHorarios);
    if (!resultado.ok) {
      alert(resultado.message ?? 'Hay horarios superpuestos.');
      return false;
    }

    return true;
  };

  const handleFormValidation = () => {
    let toValidate = [
      ...Array.from(document.getElementsByClassName('form-control')),
    ] as HTMLInputElement[];
    toValidate = toValidate.filter(e => !e.disabled && e.required);

    let missing = false;
    // Validar campos required
    toValidate.forEach(field => {
      if (field.required && !field.disabled && !field.value?.trim()) {
        field.classList.add('is-invalid');

        missing = true;
      } else {
        field.classList.remove('is-invalid');
      }
    });

    if (missing) {
      alert(
        'Todos los campos marcados con * son obligatorios. ' +
          'Por favor complete los faltantes antes de enviar la solicitud.',
      );
      return false;
    }

    // Si los campos estan bien, se revisan los horarios
    return validarHorarios();
  };

  // Confirmación de envío de Solicitud
  const handleSendForm = () => {
    if (!handleFormValidation()) {
      return;
    }

    setConfirm(
      true,
      '¿Estás seguro que deseas enviar esta solicitud de consultorio?',
      'send',
    );
  };

  // Gestor de respuestas de confirmaciones
  const handleConfirmation = (action: string) => {
    if (action === 'send') {
      handleSend();
    }

    setConfirm(false);
  };

  // Envío de solicitudes
  const handleSend = async () => {
    setLoading(true);

    // Enviamos la petición
    await putHorariosConsultorio(id, horariosAtencion)
      .then(res => {
        if (!res || res.status != 'ok') {
          // Error interno
          console.error('ConsultoriosCreate', 'Error al enviar la solicitud');
          console.info(res);
          alert(
            'Ha ocurrido un error al enviar la solicitud. Por favor intente nuevamente.',
          );
          return;
        }

        alert('La solicitud se envió correctamente', () => {
          history.push('/consultorios');
        });
      })
      .catch(err => {
        console.error(
          'ConsultoriosCreate',
          'Error inesperado al intentar enviar la solicitud',
        );
        console.info(err);
        alert(
          'Ha ocurrido un error al enviar la solicitud. Por favor intente nuevamente.',
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="content">
      <Row>
        <Col sm={12}>
          <Card>
            <CardHeader>
              <Row style={{ alignItems: 'center' }}>
                <Col sm={8}>
                  <CardTitle tag="h5">Editar Horarios del Consultorio</CardTitle>
                </Col>
                <Col sm={4} className="text-right">
                  <Button
                    className="btn-round"
                    color="primary"
                    onClick={() => history.push('/consultorios')}
                  >
                    Volver
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              <Row>
                <Col sm={12}>
                  <CardTitle>Consultorio</CardTitle>
                </Col>
              </Row>
              <Row>
                <Col sm={6}>
                  <FormGroup>
                    <label className='form-control-label'>Nombre</label>
                    <p>{ consultorio.nombreConsultorio || '-' }</p>
                  </FormGroup>
                </Col>
                <Col sm={6}>
                  <FormGroup>
                    <label className='form-control-label'>Dirección</label>
                    <p>{ consultorio.direccion } { consultorio.numero }</p>
                  </FormGroup>
                </Col>
              </Row>
              <hr className="my-4" />
              <Form innerRef={formElement}>
                <Col sm={12}>
                  <CardTitle>Atención</CardTitle>
                </Col>
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="documentacion">
                        Días y horarios de atención <Required />
                      </Label>
                      <HorariosAtencionComponent
                        id={id}
                        value={horariosAtencion}
                        actuales={otrosHorarios}
                        onChange={horarios => setHorariosAtencion(horarios)}
                        disabled={isLoading}
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <div className="text-center">
                    <Button
                      type="button"
                      className="btn-round"
                      onClick={handleSendForm}
                      disabled={isLoading || disableSend}
                    >
                      Enviar
                    </Button>
                  </div>
                </FormGroup>
              </Form>
              <AlertModal
                isOpen={showAlert}
                message={message}
                onClose={alertCallback}
              />
              <ConfirmModal
                isOpen={confirm}
                title={confirmTitle}
                action={confirmAction}
                onClose={() => setConfirm(false)}
                onConfirm={handleConfirmation}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
